import { FeatureDto, FeatureFlag, PackageTier, AddOnType } from 'fourwaves-shared';

export const featureFlagAddOnTypeMap = {
  [FeatureFlag.Reviewing]: AddOnType.PeerReviewsModule,
  [FeatureFlag.WithLiveApp]: AddOnType.VirtualPosterSessions,
  [FeatureFlag.VideoRecordings]: AddOnType.VideoRecordings,
  [FeatureFlag.UnlimitedSubmissions]: AddOnType.UnlimitedSubmissions,
  [FeatureFlag.PaymentCollection]: AddOnType.PaymentCollection,
};

const featureUpgradeMap = {
  addOns: [
    FeatureFlag.Reviewing,
    FeatureFlag.WithLiveApp,
    FeatureFlag.VideoRecordings,
    FeatureFlag.PaymentCollection,
    FeatureFlag.UnlimitedSubmissions,
  ],
  packages: {
    [PackageTier.Essentials]: [FeatureFlag.MassEmails, FeatureFlag.EnhancedContentBlocks, FeatureFlag.Badges],
    [PackageTier.Advanced]: [FeatureFlag.ContentPages, FeatureFlag.PrivateContent],
    [PackageTier.Professional]: [
      FeatureFlag.Reviewing,
      FeatureFlag.VideoRecordings,
      FeatureFlag.WhiteLabel,
      FeatureFlag.UnlimitedSubmissions,
    ],
  },
};

export const nonPaidFeatures = [FeatureFlag.AbstractBooklet]; // when there are none, leaving an empty string allow us to keep the unit tests unskipped

export function hasAllPaidFeaturesActive(eventFeatures: FeatureDto[]) {
  const paidFeatures = Object.values(FeatureFlag).filter(flag => !nonPaidFeatures.includes(flag));
  return paidFeatures.every(flag => eventFeatures.some(feature => feature.name === flag && feature.isEnabled));
}

export function getFeatureUpgradeType(feature: FeatureFlag) {
  const isAddOn = featureUpgradeMap.addOns.includes(feature);
  const [upgradePackage = null] =
    Object.entries(featureUpgradeMap.packages).find(([, features]) => features.includes(feature)) || [];

  return { isAddOn, upgradePackage: upgradePackage as PackageTier | null };
}

export function getFeatureFlagByAddOnType(addOnType: AddOnType) {
  return Object.entries(featureFlagAddOnTypeMap).find(([, type]) => type === addOnType)?.[0] || '';
}

export function getFeatureFlagByPackageTier(tier: PackageTier): FeatureFlag[] {
  return featureUpgradeMap.packages[tier];
}

export function isFeatureFees(name: string): boolean {
  return name === 'fees';
}

export interface PackageFeature {
  key: string | FeatureFlag;
  hasTooltip?: boolean;
  replaceKeyWithFlag?: FeatureFlag;
}

export const featureListByPackageTier: Record<string, PackageFeature[]> = {
  [PackageTier.Free]: [
    { key: 'unlimited_registrations' },
    { key: 'twenty_five_abstracts', replaceKeyWithFlag: FeatureFlag.UnlimitedSubmissions },
    { key: 'base_ews' },
    { key: 'fees', hasTooltip: true },
    { key: 'api' },
  ],
  [PackageTier.Essentials]: [
    { key: 'free_plus' },
    { key: 'hundred_abstracts', replaceKeyWithFlag: FeatureFlag.UnlimitedSubmissions },
    { key: FeatureFlag.MassEmails },
    { key: FeatureFlag.EnhancedContentBlocks },
    { key: FeatureFlag.Badges },
    { key: 'fees', hasTooltip: true },
  ],
  [PackageTier.Advanced]: [
    { key: 'essential_plus' },
    { key: 'two_hundred_abstracts', replaceKeyWithFlag: FeatureFlag.UnlimitedSubmissions },
    { key: FeatureFlag.ContentPages },
    { key: 'onboarding' },
    { key: FeatureFlag.PrivateContent, hasTooltip: true },
    { key: 'fees', hasTooltip: true },
  ],
  [PackageTier.Professional]: [
    { key: 'advanced_plus' },
    { key: 'vip_support' },
    { key: FeatureFlag.UnlimitedSubmissions },
    { key: FeatureFlag.WhiteLabel },
    { key: 'peer_review' },
    { key: 'video' },
    { key: 'fees', hasTooltip: true },
  ],
};

export const featureListByAddOnType: Record<string, string[]> = {
  [AddOnType.PeerReviewsModule]: ['review_1', 'review_2', 'review_3', 'review_4', 'review_5'],
  [AddOnType.VirtualPosterSessions]: ['live_app_1', 'live_app_2', 'live_app_3', 'live_app_4', 'live_app_5'],
  [AddOnType.VideoRecordings]: [],
  [AddOnType.UnlimitedSubmissions]: [],
  [AddOnType.PaymentCollection]: [],
};
